import React, { useState } from 'react';
import { useAppConfig } from '../context/AppConfigContext';
import { joinWaitlist } from '../services/geo';
import { SUPPORTED_COUNTRIES as SUPPORTED_COUNTRIES_STATIC, OTHER_COUNTRIES } from '../utils/countries';

type AnimalType = 'perro' | 'gato';

export interface CountryGatePageProps {
  /** Pre-filled country code detected from the backend */
  detectedCountry?: string | null;
  /** Codes of countries where the funnel is available (from geoResult.supported_countries) */
  supportedCountries?: string[];
  /** Called when the user corrects their country to a supported one — triggers gate bypass */
  onCountryOverride?: (code: string) => void;
  /** Called after a successful waitlist submission (mostly useful for tests) */
  onSave?: (payload: {
    email: string;
    petName: string;
    animalType: AnimalType;
    country: string;
    petWeightKg?: number;
  }) => void;
}

export const CountryGatePage: React.FC<CountryGatePageProps> = ({
  detectedCountry,
  supportedCountries = [],
  onCountryOverride,
  onSave,
}) => {
  const { t, logoImage } = useAppConfig();

  const ALL_COUNTRIES = [...SUPPORTED_COUNTRIES_STATIC, ...OTHER_COUNTRIES];
  const detectedCountryName = detectedCountry
    ? (ALL_COUNTRIES.find((c) => c.code === detectedCountry)?.name ?? detectedCountry)
    : null;
  // correctionCountry updates as the user changes the selector — use it for the title
  const displayCountryName = (code: string) => ALL_COUNTRIES.find((c) => c.code === code)?.name ?? code;

  // Form state
  const [animalType, setAnimalType] = useState<AnimalType | null>(null);
  const [petName, setPetName] = useState('');
  const [petWeightKg, setPetWeightKg] = useState('');
  const [country, setCountry] = useState(detectedCountry ?? '');
  const [correctionCountry, setCorrectionCountry] = useState(detectedCountry ?? '');
  const [email, setEmail] = useState('');

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleCorrectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const code = e.target.value;
    setCorrectionCountry(code);
    if (supportedCountries.includes(code)) {
      onCountryOverride?.(code);
    } else if (code) {
      setCountry(code);
    }
  };

  // Validation
  const weightRequired = animalType === 'gato';
  const weightValid = !weightRequired || (petWeightKg.trim() !== '' && Number(petWeightKg) > 0);
  const canSubmit =
    animalType !== null &&
    petName.trim().length > 0 &&
    country.trim().length > 0 &&
    email.trim().length > 0 &&
    email.includes('@') &&
    weightValid;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !animalType) return;

    setSubmitting(true);
    setSubmitError('');

    const payload = {
      email: email.trim(),
      petName: petName.trim(),
      animalType,
      country,
      ...(weightRequired && petWeightKg.trim() ? { petWeightKg: Number(petWeightKg) } : {}),
    };

    try {
      const res = await joinWaitlist(payload);
      setAlreadyRegistered(!!res?.already_registered);
      onSave?.(payload);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(t('countrygate.submit_error', 'Hubo un problema al guardar tus datos. Inténtalo de nuevo.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F4EE] flex flex-col">
      {/* Header */}
      <header className="bg-white px-4 sm:px-6 py-2 flex items-center flex-shrink-0 shadow-sm">
        <img src={logoImage} alt="Milo &amp; Lola" className="h-8 sm:h-10 object-contain" />
      </header>

      {/* Main content */}
      <main className="flex-1 px-4 py-8 max-w-xl mx-auto w-full">
        {/* Hero */}
        <div className="text-center mb-8">
          {detectedCountryName ? (
            <>
              <h1 className="font-fredoka text-2xl sm:text-3xl font-bold text-[#008BBC] leading-snug mb-3">
                {t('countrygate.title_detected', 'Hola 👋 Parece que accedes desde')}{' '}
                <strong>{displayCountryName(correctionCountry || detectedCountry || '')}</strong>
              </h1>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mt-3">
                <label htmlFor="cg-correction" className="font-fredoka text-sm text-black/60 whitespace-nowrap">
                  {t('countrygate.correction_label', '¿No es tu país?')}
                </label>
                <select
                  id="cg-correction"
                  value={correctionCountry}
                  onChange={handleCorrectionChange}
                  className="px-3 py-1.5 bg-white rounded-lg border border-gray-200 font-fredoka text-sm focus:outline-none focus:ring-2 focus:ring-[#008BBC]/40 appearance-none"
                >
                  <option value="">{t('countrygate.correction_placeholder', '— Selecciona tu país —')}</option>
                  <optgroup label={t('countrygate.countries_available', 'Disponible')}>
                    {ALL_COUNTRIES.filter((c) => supportedCountries.includes(c.code)).map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label={t('countrygate.countries_coming_soon', 'Próximamente')}>
                    {ALL_COUNTRIES.filter((c) => !supportedCountries.includes(c.code)).map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </div>
            </>
          ) : (
            <>
              <h1 className="font-fredoka text-2xl sm:text-3xl font-bold text-[#008BBC] leading-snug mb-3">
                {t('countrygate.title', 'Aún no llegamos a tu país, pero queremos saber de tu peludo 🐾')}
              </h1>
              <p className="font-fredoka text-base sm:text-lg text-black/70 max-w-xl mx-auto leading-relaxed">
                {t('countrygate.subtitle', 'Déjanos tus datos y te avisaremos en cuanto podamos servirte.')}
              </p>
            </>
          )}
        </div>

        {/* Waitlist form */}
        <div className="bg-white rounded-[20px] p-6 sm:p-8 shadow-sm">
          {submitted ? (
            /* Thank-you state */
            <div className="text-center py-6 space-y-4">
              <div className="text-5xl">🐾</div>
              <h2 className="font-fredoka text-xl font-bold text-[#008BBC]">
                {t('countrygate.thanks', '¡Gracias, {petName}! Te avisaremos en cuanto podamos servirte 🐾', {
                  petName: petName.trim(),
                })}
              </h2>
              <p className="font-fredoka text-sm text-black/60">
                {alreadyRegistered
                  ? t('countrygate.thanks_sub_updated', 'Ya estabas en nuestra lista; hemos actualizado tus datos.')
                  : t('countrygate.thanks_sub', 'Hemos guardado tu email. ¡Hasta pronto!')}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate className="space-y-5">
              <h2 className="font-fredoka text-lg font-semibold text-black mb-1">
                {t('countrygate.form_heading', 'Cuéntanos sobre tu peludo')}
              </h2>

              {/* Animal type */}
              <fieldset>
                <legend className="font-fredoka text-sm font-medium text-black/70 mb-2 block">
                  {t('countrygate.animal_label', '¿Perro o gato?')}
                </legend>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    aria-pressed={animalType === 'perro'}
                    onClick={() => setAnimalType('perro')}
                    className={`flex flex-col items-center gap-1.5 py-4 rounded-[16px] border-2 font-fredoka text-base font-medium transition-all ${
                      animalType === 'perro'
                        ? 'border-[#008BBC] bg-[#e5f3f8] text-[#008BBC]'
                        : 'border-gray-200 bg-gray-50 text-black hover:border-[#008BBC]/40'
                    } ${animalType === 'gato' ? 'opacity-40' : ''}`}
                  >
                    <img
                      src={t('countrygate.animal_dog_image', '/images/funnel/dog-illustration.png')}
                      alt={t('countrygate.animal_dog', 'Perro')}
                      className="w-12 h-12 object-contain"
                    />
                    <span>{t('countrygate.animal_dog', 'Perro')}</span>
                  </button>
                  <button
                    type="button"
                    aria-pressed={animalType === 'gato'}
                    onClick={() => setAnimalType('gato')}
                    className={`flex flex-col items-center gap-1.5 py-4 rounded-[16px] border-2 font-fredoka text-base font-medium transition-all ${
                      animalType === 'gato'
                        ? 'border-[#008BBC] bg-[#e5f3f8] text-[#008BBC]'
                        : 'border-gray-200 bg-gray-50 text-black hover:border-[#008BBC]/40'
                    } ${animalType === 'perro' ? 'opacity-40' : ''}`}
                  >
                    <img
                      src={t('countrygate.animal_cat_image', '/images/funnel/cat-illustration.png')}
                      alt={t('countrygate.animal_cat', 'Gato')}
                      className="w-12 h-12 object-contain"
                    />
                    <span>{t('countrygate.animal_cat', 'Gato')}</span>
                  </button>
                </div>
              </fieldset>

              {/* Pet name */}
              <div className="space-y-1.5">
                <label htmlFor="cg-pet-name" className="font-fredoka text-sm font-medium text-black/70 block">
                  {t('countrygate.name_label', '¿Cómo se llama?')}
                </label>
                <input
                  id="cg-pet-name"
                  type="text"
                  value={petName}
                  onChange={(e) => setPetName(e.target.value)}
                  placeholder={t('countrygate.name_placeholder', 'Nombre de tu peludo')}
                  aria-required="true"
                  className="w-full px-4 py-3 bg-gray-100 rounded-xl font-fredoka text-base placeholder:text-black/30 focus:outline-none focus:ring-2 focus:ring-[#008BBC]/40 focus:bg-white transition-all"
                />
              </div>

              {/* Weight — only for cats */}
              {animalType === 'gato' && (
                <div className="space-y-1.5">
                  <label htmlFor="cg-weight" className="font-fredoka text-sm font-medium text-black/70 block">
                    {t('countrygate.weight_label', 'Peso aproximado (kg)')}
                  </label>
                  <input
                    id="cg-weight"
                    type="number"
                    min="0.5"
                    max="30"
                    step="0.1"
                    value={petWeightKg}
                    onChange={(e) => setPetWeightKg(e.target.value)}
                    placeholder={t('countrygate.weight_placeholder', 'p.ej. 4.5')}
                    aria-required="true"
                    className="w-full px-4 py-3 bg-gray-100 rounded-xl font-fredoka text-base placeholder:text-black/30 focus:outline-none focus:ring-2 focus:ring-[#008BBC]/40 focus:bg-white transition-all"
                  />
                </div>
              )}

              {/* Country */}
              <div className="space-y-1.5">
                <label htmlFor="cg-country" className="font-fredoka text-sm font-medium text-black/70 block">
                  {t('countrygate.country_label', 'Tu país')}
                </label>
                <select
                  id="cg-country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  aria-required="true"
                  className="w-full px-4 py-3 bg-gray-100 rounded-xl font-fredoka text-base focus:outline-none focus:ring-2 focus:ring-[#008BBC]/40 focus:bg-white transition-all appearance-none"
                >
                  <option value="">{t('countrygate.country_placeholder', '— Selecciona tu país —')}</option>
                  {/* Supported countries first */}
                  <optgroup label={t('countrygate.countries_available', 'Disponible')}>
                    {ALL_COUNTRIES.filter((c) => supportedCountries.includes(c.code)).map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </optgroup>
                  {/* Rest */}
                  <optgroup label={t('countrygate.countries_coming_soon', 'Próximamente')}>
                    {ALL_COUNTRIES.filter((c) => !supportedCountries.includes(c.code)).map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <label htmlFor="cg-email" className="font-fredoka text-sm font-medium text-black/70 block">
                  {t('countrygate.email_label', 'Tu email')}
                </label>
                <input
                  id="cg-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('countrygate.email_placeholder', 'hola@ejemplo.com')}
                  aria-required="true"
                  className="w-full px-4 py-3 bg-gray-100 rounded-xl font-fredoka text-base placeholder:text-black/30 focus:outline-none focus:ring-2 focus:ring-[#008BBC]/40 focus:bg-white transition-all"
                />
              </div>

              {/* Error */}
              {submitError && (
                <p role="alert" className="font-fredoka text-sm text-red-600">
                  {submitError}
                </p>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={!canSubmit || submitting}
                className={`w-full py-3.5 rounded-xl font-fredoka text-lg font-medium transition-all ${
                  canSubmit && !submitting
                    ? 'bg-[#008BBC] text-white hover:bg-[#007aa8] shadow-md'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {submitting
                  ? t('countrygate.submitting', 'Guardando...')
                  : t('countrygate.submit', 'Avísame cuando lleguéis')}
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
};
